// packages/app/src/modules/nav/useSidebarBadges.ts
import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import { scaffolderApiRef } from '@backstage/plugin-scaffolder-react';
import { useSignal } from '@backstage/plugin-signals-react';
import { useApprovalsApi } from '../approvals/api';
import { needsMyApproval, toMyKeys } from '../approvals/identity';
import { useArchiveApi } from '../archive/useArchiveApi';

/** Fallback poll when no signal arrives (real-time signals do the heavy work). */
const POLL_MS = 60_000;

/**
 * A live count for a sidebar badge: refreshed in real time by the given signal
 * channel, with a poll / window-focus / navigation fallback. `fetchCount` must
 * be stable (wrap in useCallback).
 */
function useLiveCount(
  fetchCount: () => Promise<number>,
  channel: string,
): number {
  const { pathname } = useLocation();
  const { lastSignal } = useSignal(channel);
  const [count, setCount] = useState(0);

  const refresh = useCallback(() => {
    fetchCount()
      .then(setCount)
      .catch(() => {});
  }, [fetchCount]);

  useEffect(() => {
    refresh();
    window.addEventListener('focus', refresh);
    const timer = window.setInterval(refresh, POLL_MS);
    return () => {
      window.removeEventListener('focus', refresh);
      window.clearInterval(timer);
    };
  }, [refresh, pathname]);

  // Real-time: refetch whenever the backend signals a change on the channel.
  useEffect(() => {
    if (lastSignal) refresh();
  }, [lastSignal, refresh]);

  return count;
}

/**
 * "Needs my approval" count — real-time via the `approvals` signal. Counts only
 * pending requests that the current user can approve (matching the list page's
 * "Needs my approval" filter), and excludes approvals the user has archived
 * (archive is per-user/client-side, so the backend count would otherwise
 * include them).
 */
export function usePendingApprovalsCount(): number {
  const { list } = useApprovalsApi();
  const identityApi = useApi(identityApiRef);
  const archiveApi = useArchiveApi();
  const fetchCount = useCallback(async () => {
    const [{ requests }, archived, identity] = await Promise.all([
      list({ status: 'pending', limit: 200, offset: 0 }),
      archiveApi.list('approval').catch(() => new Set<string>()),
      identityApi.getBackstageIdentity().catch(() => undefined),
    ]);
    const myKeys = toMyKeys(identity?.ownershipEntityRefs);
    return requests.filter(
      r => !archived.has(r.id) && needsMyApproval(r, myKeys),
    ).length;
  }, [list, archiveApi, identityApi]);
  return useLiveCount(fetchCount, 'approvals');
}

/**
 * In-progress runs count *run by me* (my scaffolder tasks still open/processing),
 * excluding runs the user has archived. The scaffolder `listTasks` API has no
 * status filter — only `filterByOwnership` — so we scope to `'owned'` (matches
 * the caller's ownership refs against `created_by`) and filter open/processing
 * client-side. Subscribes to a `runs` signal if one is ever emitted; otherwise
 * the poll fallback keeps it fresh (the scaffolder doesn't emit one today).
 */
export function useInProgressRunsCount(): number {
  const scaffolderApi = useApi(scaffolderApiRef);
  const archiveApi = useArchiveApi();
  const fetchCount = useCallback(async () => {
    const [res, archived] = await Promise.all([
      scaffolderApi.listTasks?.({ filterByOwnership: 'owned' }),
      archiveApi.list('run').catch(() => new Set<string>()),
    ]);
    return (res?.tasks ?? []).filter(
      (t: { id?: string; status?: string }) =>
        (t.status === 'open' || t.status === 'processing') &&
        !archived.has(t.id ?? ''),
    ).length;
  }, [scaffolderApi, archiveApi]);
  return useLiveCount(fetchCount, 'runs');
}
